import axios, { AxiosResponse } from "axios";
import { companyConfig } from "../globalContext";
import { ILedger } from "../types/ILedger";
import { IPaymentMethod } from "../types/PaymentMethod";
import demoAgent from "./demo/demoAgent";
import { AccessPoint } from "../providers/account-provider/AccountProvider";
import { ApiResult } from "./responses/ApiResult";
import { CommandResponse } from "./responses/CommandResponse";
import { OpenGateWithGateCodeRequest } from "../types/OpenGateWithGateCodeRequest";
import { OpenGateRequest } from "../types/OpenGateRequest";
import { ApiBaseReponse } from "../types/shared";

axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*";

axios.defaults.withCredentials = true;

const responseBody = <T>(response: AxiosResponse<T>) => {
  return response.data;
};

export const storageKeys = {
  phoneToken: "phoneToken",
};

let accessToken = localStorage.getItem(storageKeys.phoneToken) || "";
export const getToken = () =>
  accessToken || localStorage.getItem(storageKeys.phoneToken) || "";
export const setLocalStorageToken = (token: string) => {
  accessToken = token;
};

const formatError = (err: any) => {
  if (err?.response?.data?.errors) {
    return Object.values(err?.response?.data?.errors)[0];
  }
  if (err?.response?.data?.status) {
    return err?.response?.data;
  }
  return err?.response?.data || JSON.stringify(err);
};

const requests = {
  get: async <T>(url: string): Promise<T> => {
    return (await axios
      .get<T>(url, {
        headers: {
          PhoneToken: getToken(),
        },
      })
      .then(responseBody)
      .catch((err: any) => ({ error: formatError(err) }))) as T;
  },
  post: <T>(url: string, body: {}) =>
    axios
      .post<T>(url, body, {
        headers: {
          PhoneToken: getToken(),
        },
      })
      .then(responseBody)
      .catch((error) => {
        // TODO: format the different errors and log to sentry accordingly and display appropriate response to user
        return {
          error: error.response?.data?.toString(),
        };
      }),
  postImage: <T>(url: string, body: {}) =>
    axios
      .post<T>(url, body, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(responseBody),
  put: <T>(url: string, body: {}) =>
    axios
      .put<T>(url, body, {
        headers: {
          PhoneToken: getToken(),
        },
      })
      .then(responseBody),
  delete: <T>(url: string) =>
    axios
      .delete<T>(url, {
        headers: {
          PhoneToken: getToken(),
        },
      })
      .then(responseBody),
};

const Facility = {
  getFacilities: () => requests.get<any>("/api/Facilities/details"),
  getLedgerByPhoneNumber: (
    phoneNumber: string,
    facilityID: string
  ): Promise<any> =>
    requests.get<any>(
      `/GetTenantLedgers/${facilityID}?phoneNumber=${phoneNumber}&companyID=${companyConfig.companyApiId}`
    ),
  initiateMoveOut: (imageData: FormData) =>
    requests.postImage<any>("/api/facilities/initiate-move-out", imageData),
  initiateRemoteMoveOut: (imageData: any) =>
    requests.post<any>("/api/facilities/initiate-remote-move-out", imageData),
  initiateAuctionMoveOut: (imageData: FormData) =>
    requests.postImage<any>(
      "/api/facilities/initiate-auction-move-out",
      imageData
    ),
};

const Account = {
  getAccount: () =>
    requests.get<{ data: ILedger[] }>(`/api/accounts/auth-user/ledgers`),
  getLedgerByPhoneNumber: (
    phoneNumber: string,
    facilityID: string
  ): Promise<any> =>
    requests.get<any>(
      `/api/accounts/ledgers/${facilityID}?phoneNumber=${phoneNumber}`
    ),
};

const Access = {
  getAccessPoints: async (facilityID: string) => {
    
    const [accessPointsResponse, goatsResponse] = await Promise.all([
      requests.get<ApiResult<AccessPoint[]>>(
        `/api/access-points/facilities/${facilityID}`
      ),
      requests.get<ApiResult<any[]>>(
        `/api/goats/facility/${facilityID}`
      )
    ]);

    // Transform goats to match AccessPoint interface structure
    const transformedGoats: AccessPoint[] = (goatsResponse.data || []).map((goat: any) => ({
      id: goat.id.toString(),
      name: goat.name,
      facilityId: goat.facilityId,
      hardwareType: goat.hardwareType,
      simSid: '', // Goats don't have simSid in the same way
      companyId: goat.companyId,
      lastTestedTimestamp: goat.createdAt || new Date().toISOString(),
      signalStrength: 0, // Default value, can be updated if available
      isActive: goat.isActive
    }));

    // Combine access points and transformed goats into a single array
    const combinedData = [
      ...(accessPointsResponse.data || []),
      ...transformedGoats
    ];

    return {
      ...accessPointsResponse,
      data: combinedData
    };
  },
  openAccessPoint: (payload: OpenGateRequest) =>
    requests.post<ApiResult<CommandResponse>>(
      `/api/access-points/open`,
      payload
    ),
  openAccessPointWithGateCode: (payload: OpenGateWithGateCodeRequest) =>
    requests.post<ApiResult<CommandResponse>>(
      `/api/access-points/open-with-code`,
      payload
    ),
  openAccessPointWithGateCodeOnly: (payload: OpenGateWithGateCodeRequest) =>
    requests.post<ApiResult<CommandResponse>>(
      `/api/access-points/open-with-code-only`,
      payload
    ),
  getRecentAccessFailedAttempts: (facilityID: string, accessID: string) =>
    requests.get<ApiResult<number>>(
      `/api/access/access-points/${facilityID}/recent-attempts/${accessID}`
    ),
  getLocks: (facilityID: string) =>
    requests.get<any>(`/api/unit-locks/facilities/${facilityID}`),
  getTenantOwnedLocks: (facilityID: string) =>
    requests.get<any>(`/api/unit-locks/tenant/facilities/${facilityID}`),
};

const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const query = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== "")
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join("&");

  return query ? `?${query}` : "";
};

const Goat = {
  openGoat: (id: number, gateCode: string, latitude?: number, longitude?: number) => {
    const query = buildQuery({ gateCode, latitude, longitude });
    return requests.get<CommandResponse>(`/api/goats/${id}/open${query}`);
  },

  openGoatWithCodeOnly: (id: number, gateCode: string, phoneNumber?: string, latitude?: number, longitude?: number) => {
    const query = buildQuery({ gateCode, phoneNumber, latitude, longitude });
    return requests.get<CommandResponse>(`/api/goats/${id}/open-with-code-only${query}`);
  },

  openAndHoldGoat: (id: number, latitude?: number, longitude?: number) => {
    const query = buildQuery({ latitude, longitude });
    return requests.get<CommandResponse>(`/api/goats/${id}/open-and-hold${query}`);
  },

  closeGoat: (id: number, latitude?: number, longitude?: number) => {
    const query = buildQuery({ latitude, longitude });
    return requests.get<CommandResponse>(`/api/goats/${id}/close${query}`);
  },

  getGoatStatus: (id: number) =>
    requests.get<ApiResult<CommandResponse>>(
      `/api/goats/${id}/status`
    ),
  getSignalStrength: (id: number) =>
    requests.get<ApiResult<CommandResponse>>(
      `/api/goats/${id}/signal-strength`
    ),
  getRecentFailedAttempts: (id: number, phoneNumber?: string) => {
    const query = buildQuery({ phoneNumber });
    return requests.get<ApiResult<number>>(
      `/api/goats/${id}/recent-attempts${query}`
    );
  },
};

const OTP = {
  sendOTP: (phoneNumber: string) =>
    axios
      .post<string>(
        "/api/auth/otp/send?phoneNumber=" + phoneNumber,
        {},
        { withCredentials: true }
      )
      .then(responseBody),
  resendOTP: () =>
    axios
      .post<string>("/api/auth/otp/resend", {}, { withCredentials: true })
      .then(responseBody),
  verifyOTP: (otp: string) =>
    requests.post<ApiBaseReponse<boolean>>(
      `/api/auth/otp/verify?otp=${otp}`,
      {}
    ),

  bypassOtp: () => requests.post<any>(`/api/auth/otp/bypass`, {}),
};

const Auth = {
  logout: () =>
    axios
      .get<string>("/api/auth/logout", { withCredentials: true })
      .then(responseBody),
};

const Payments = {
  getLedgerEligibility: (
    facilityId: string,
    ledgerId: string
  ): Promise<{
    data: {
      eligibleForPayment: boolean;
    };
  }> =>
    requests.get<any>(
      `/api/payments/${facilityId}/ledger-eligibility/${ledgerId}`
    ),
  getLedgerPrepayAmount: (
    facilityId: string,
    ledgerId: string,
    prepayMonths: number
  ): Promise<{
    data: {
      amount: number;
    };
  }> =>
    requests.get<any>(
      `/api/payments/${facilityId}/ledger-prepay-amount/${ledgerId}?prepayMonths=${prepayMonths}`
    ),
  getPaymentMethods: (
    facilityId: string,
    tenantId: string,
    ledgerId: string
  ): Promise<{ data: IPaymentMethod[] }> =>
    requests.get<any>(
      `/api/payments/payment-methods/${facilityId}?tenantId=${tenantId}&ledgerId=${ledgerId}`
    ),
  getAllowedPrePayMonths: (): Promise<{ data: number }> =>
    requests.get<any>(`/api/settings/prepay-months`),
};

const Logger = {
  logError: (data: { message: string; type?: number }) =>
    requests.post<any>("/api/logs/log-error", data),
};

const agents = {
  Account,
  Access,
  Goat,
  Facility,
  Auth,
  Payments,
  Logger,
  OTP,
  ...(_context.companyConfig.subdomain === "demo" ? demoAgent : {}),
};

export default agents;
